import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { content, firmInfo } from '../mock';
import { Phone, Mail, MapPin, ArrowUp } from 'lucide-react';

const Footer = () => {
  const { language } = useLanguage();
  const t = content[language];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-slate-600/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/aldurar-logo.png" 
                alt="AL-DURAR LAW FIRM Logo" 
                className="h-16 w-auto object-contain bg-white rounded-lg p-2"
              />
            </div>
            <p className="text-slate-300 mb-6 leading-relaxed max-w-md">
              {t.footer.description}
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-slate-300">
                <Phone className="w-5 h-5 text-amber-500" />
                <a href={`tel:${firmInfo.phone}`} className="hover:text-amber-400 transition-colors">
                  {firmInfo.phone}
                </a>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <Mail className="w-5 h-5 text-amber-500" />
                <a href={`mailto:${firmInfo.email}`} className="hover:text-amber-400 transition-colors break-all">
                  {firmInfo.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <MapPin className="w-5 h-5 text-amber-500" />
                <span>{t.contact.locationDetail}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t.footer.quickLinks}</h3>
            <ul className="space-y-3">
              <li>
                <button onClick={() => scrollToSection('home')} className="text-slate-300 hover:text-amber-400 transition-colors">
                  {t.nav.home}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="text-slate-300 hover:text-amber-400 transition-colors">
                  {t.nav.about}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-amber-400 transition-colors">
                  {t.nav.services}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('why-us')} className="text-slate-300 hover:text-amber-400 transition-colors">
                  {t.nav.whyUs}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-amber-400 transition-colors">
                  {t.nav.contact}
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t.services.title}</h3>
            <ul className="space-y-3">
              {t.services.items.slice(0, 5).map((service) => (
                <li key={service.id}>
                  <span className="text-slate-300 text-sm">{service.title}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">{t.footer.copyright}</p>
            <button
              onClick={scrollToTop}
              className="bg-amber-500 hover:bg-amber-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-110"
              aria-label="Scroll to top"
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;